'use client';

import { useEffect } from 'react';
import { useRouter, usePathname } from 'next/navigation';
import { useAuth } from '@/contexts/AuthContext';
import { LoginSkeleton } from '@/components/skeletons/LoginSkeleton';
import { StudentProvider } from '@/contexts/StudentContext';

interface AppAuthGuardProps {
  children: React.ReactNode;
}

// Routes that don't require authentication
// Note: usePathname() returns the path without the basePath, so we don't include /parent here
const PUBLIC_ROUTES = ['/login', '/forgot-password', '/reset-password'];

export function AppAuthGuard({ children }: AppAuthGuardProps) {
  const { user, token, isLoading } = useAuth();
  const router = useRouter();
  const pathname = usePathname();

  const isAuthenticated = !!token && !!user && user.role === 'PARENT';
  const isPublicRoute = PUBLIC_ROUTES.includes(pathname);

  useEffect(() => {
    // Don't redirect during loading phase
    if (isLoading) return;

    // If on a public route, don't redirect regardless of auth state
    if (isPublicRoute) return;

    // Only redirect if not authenticated and not on a public route
    if (!isAuthenticated) {
      console.log('Redirecting to login - not authenticated');
      router.replace('/login');
      return;
    }

    // If authenticated and on login page, redirect to dashboard
    //   if (isAuthenticated && pathname === '/login') {
    //     console.log('Redirecting to dashboard - already authenticated');
    //     router.replace('/parent');
    //     return;
    //   }
  }, [isLoading, isAuthenticated, isPublicRoute, pathname, router]);

  // Show loading skeleton while checking authentication
  if (isLoading) {
    return <LoginSkeleton />;
  }

  // If not authenticated and not on a public route, show loading while redirecting
  if (!isAuthenticated && !isPublicRoute) {
    return <LoginSkeleton />;
  }

  // Render the content (either authenticated pages or public pages)
  return (
    <>
      {isAuthenticated ? (
        <StudentProvider>{children}</StudentProvider>
      ) : (
        children
      )}
    </>
  );
}
